/**
 * Comprehensive Input Validation & Sanitization
 *
 * Security-focused validation for all user inputs to prevent:
 * - XSS (Cross-Site Scripting)
 * - SQL Injection
 * - NoSQL Injection
 * - Command Injection
 * - Path Traversal
 * - Data corruption
 *
 * @module inputValidation
 */

/**
 * HTML Entity Encoding Map
 */
const HTML_ENTITIES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
};

/**
 * Dangerous patterns to detect
 */
const DANGEROUS_PATTERNS = {
  script: /<script[^>]*>[\s\S]*?<\/script>/gi,
  iframe: /<iframe[^>]*>[\s\S]*?<\/iframe>/gi,
  object: /<object[^>]*>[\s\S]*?<\/object>/gi,
  embed: /<embed[^>]*>/gi,
  javascript: /javascript:/gi,
  onerror: /onerror\s*=/gi,
  onclick: /on\w+\s*=/gi,
  eval: /eval\s*\(/gi,
  expression: /expression\s*\(/gi,
};

/**
 * Sanitize HTML by escaping special characters
 * Prevents XSS attacks
 *
 * @param {string} text - Text to sanitize
 * @returns {string} - Sanitized text
 */
export function sanitizeHtml(text) {
  if (typeof text !== 'string') return '';
  return text.replace(/[&<>"']/g, (char) => HTML_ENTITIES[char] || char);
}

/**
 * Sanitize generic input fields (trim, escape HTML, normalize casing)
 * @param {string} value - Raw input value
 * @param {Object} options
 * @param {boolean} [options.lowerCase=false] - Convert to lowercase
 * @param {boolean} [options.collapseWhitespace=true] - Collapse sequential whitespace
 * @returns {string}
 */
export function sanitizeInput(value, { lowerCase = false, collapseWhitespace = true } = {}) {
  if (typeof value !== 'string') {
    return '';
  }

  let sanitized = value;
  if (collapseWhitespace) {
    sanitized = sanitized.replace(/\s+/g, ' ');
  }

  sanitized = sanitizeHtml(sanitized.trim());

  if (lowerCase) {
    sanitized = sanitized.toLowerCase();
  }

  return sanitized;
}

/**
 * Strip all HTML tags from text
 * More aggressive than sanitizeHtml
 *
 * @param {string} text - Text to strip
 * @returns {string} - Text without HTML
 */
export function stripHtml(text) {
  if (typeof text !== 'string') return '';
  return text.replace(/<[^>]*>/g, '');
}

/**
 * Detect dangerous content
 * Returns true if dangerous patterns are found
 *
 * @param {string} text - Text to check
 * @returns {boolean}
 */
export function hasDangerousContent(text) {
  if (typeof text !== 'string') return false;

  for (const [name, pattern] of Object.entries(DANGEROUS_PATTERNS)) {
    if (pattern.test(text)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[Security] Dangerous pattern detected: ${name}`);
      }
      return true;
    }
  }

  return false;
}

/**
 * Validate and sanitize email address
 *
 * @param {string} email - Email to validate
 * @returns {Object} - { valid: boolean, sanitized: string, error?: string }
 */
export function validateEmail(email) {
  if (!email || typeof email !== 'string') {
    return { valid: false, sanitized: '', error: 'Email is required' };
  }

  // Trim and lowercase
  const sanitized = email.trim().toLowerCase();

  // Check length
  if (sanitized.length === 0) {
    return { valid: false, sanitized: '', error: 'Email is required' };
  }

  if (sanitized.length > 254) {
    return { valid: false, sanitized, error: 'Email is too long' };
  }

  // Strict-enough email regex for app usage; rejects angle brackets and other HTML-ish chars.
  const emailRegex = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i;
  if (!emailRegex.test(sanitized)) {
    return { valid: false, sanitized, error: 'Invalid email format' };
  }

  // Extra hard block: any raw HTML-ish characters should fail even if regex changes later.
  if (/[<>"'`]/.test(sanitized)) {
    return { valid: false, sanitized, error: 'Email contains invalid characters' };
  }

  return { valid: true, sanitized };
}

/**
 * Validate password strength
 *
 * @param {string} password - Password to validate
 * @param {Object} options - Validation options
 * @returns {Object} - { valid: boolean, strength: string, error?: string }
 */
export function validatePassword(password, options = {}) {
  const {
    minLength = 8,
    requireUppercase = true,
    requireLowercase = true,
    requireNumbers = true,
    requireSpecialChars = false,
  } = options;

  if (!password || typeof password !== 'string') {
    return { valid: false, strength: 'none', error: 'Password is required' };
  }

  // Check length
  if (password.length < minLength) {
    return {
      valid: false,
      strength: 'weak',
      error: `Password must be at least ${minLength} characters`,
    };
  }

  if (password.length > 128) {
    return { valid: false, strength: 'weak', error: 'Password is too long' };
  }

  // Check requirements
  const checks = [];

  if (requireUppercase && !/[A-Z]/.test(password)) {
    checks.push('uppercase letter');
  }

  if (requireLowercase && !/[a-z]/.test(password)) {
    checks.push('lowercase letter');
  }

  if (requireNumbers && !/[0-9]/.test(password)) {
    checks.push('number');
  }

  if (requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    checks.push('special character');
  }

  if (checks.length > 0) {
    return {
      valid: false,
      strength: 'weak',
      error: `Password must contain: ${checks.join(', ')}`,
    };
  }

  // Determine strength
  let strength = 'medium';
  if (password.length >= 12 && /[A-Z]/.test(password) && /[a-z]/.test(password) && /[0-9]/.test(password) && /[!@#$%^&*]/.test(password)) {
    strength = 'strong';
  }

  return { valid: true, strength };
}

/**
 * Validate and sanitize text input
 *
 * @param {string} text - Text to validate
 * @param {Object} options - Validation options
 * @returns {Object} - { valid: boolean, sanitized: string, error?: string }
 */
export function validateText(text, options = {}) {
  const {
    required = false,
    minLength = 0,
    maxLength = 10000,
    allowHtml = false,
    trim = true,
  } = options;

  if (text === null || text === undefined) {
    if (required) {
      return { valid: false, sanitized: '', error: 'This field is required' };
    }
    return { valid: true, sanitized: '' };
  }

  if (typeof text !== 'string') {
    return { valid: false, sanitized: '', error: 'Invalid text format' };
  }

  // Trim if requested
  let sanitized = trim ? text.trim() : text;

  // Check required
  if (required && sanitized.length === 0) {
    return { valid: false, sanitized, error: 'This field is required' };
  }

  // Check length
  if (sanitized.length < minLength) {
    return {
      valid: false,
      sanitized,
      error: `Must be at least ${minLength} characters`,
    };
  }

  if (sanitized.length > maxLength) {
    return {
      valid: false,
      sanitized,
      error: `Must be at most ${maxLength} characters`,
    };
  }

  // Sanitize HTML if not allowed
  if (!allowHtml) {
    const hadDangerous = hasDangerousContent(sanitized);

    // If there is dangerous markup, we still sanitize, but we fail when the content looks like an
    // injection attempt (no meaningful non-markup content, or obvious JS-like payload).
    if (hadDangerous) {
      const stripped = stripHtml(sanitized).trim();
      const looksLikeJsPayload = /\b(alert|eval|document|window|javascript)\b|=>|\bfunction\b/i.test(stripped);

      if (stripped.length === 0 || looksLikeJsPayload) {
        return { valid: false, sanitized, error: 'Text contains dangerous content' };
      }
    }

    sanitized = sanitizeHtml(sanitized);
  }

  return { valid: true, sanitized };
}

/**
 * Validate and sanitize URL
 *
 * @param {string} url - URL to validate
 * @param {Object} options - Validation options
 * @returns {Object} - { valid: boolean, sanitized: string, error?: string }
 */
export function validateUrl(url, options = {}) {
  const { allowedProtocols = ['http:', 'https:'], required = false } = options;

  if (!url || typeof url !== 'string') {
    if (required) {
      return { valid: false, sanitized: '', error: 'URL is required' };
    }
    return { valid: true, sanitized: '' };
  }

  const sanitized = url.trim();

  if (sanitized.length === 0) {
    if (required) {
      return { valid: false, sanitized, error: 'URL is required' };
    }
    return { valid: true, sanitized };
  }

  // Check for dangerous protocols
  if (sanitized.toLowerCase().startsWith('javascript:')) {
    return { valid: false, sanitized, error: 'Invalid URL protocol' };
  }

  // Try to parse URL
  try {
    const parsed = new URL(sanitized);

    // Check protocol
    if (!allowedProtocols.includes(parsed.protocol)) {
      return {
        valid: false,
        sanitized,
        error: `Protocol must be one of: ${allowedProtocols.join(', ')}`,
      };
    }

    return { valid: true, sanitized };
  } catch (error) {
    return { valid: false, sanitized, error: 'Invalid URL format' };
  }
}

/**
 * Validate number
 *
 * @param {*} value - Value to validate
 * @param {Object} options - Validation options
 * @returns {Object} - { valid: boolean, value: number, error?: string }
 */
export function validateNumber(value, options = {}) {
  const { min, max, integer = false, required = false } = options;

  if (value === null || value === undefined || value === '') {
    if (required) {
      return { valid: false, value: null, error: 'This field is required' };
    }
    return { valid: true, value: null };
  }

  const num = Number(value);

  if (isNaN(num)) {
    return { valid: false, value: null, error: 'Must be a valid number' };
  }

  if (!isFinite(num)) {
    return { valid: false, value: null, error: 'Number must be finite' };
  }

  if (integer && !Number.isInteger(num)) {
    return { valid: false, value: num, error: 'Must be an integer' };
  }

  if (min !== undefined && num < min) {
    return { valid: false, value: num, error: `Must be at least ${min}` };
  }

  if (max !== undefined && num > max) {
    return { valid: false, value: num, error: `Must be at most ${max}` };
  }

  return { valid: true, value: num };
}

/**
 * Validate date
 *
 * @param {*} value - Value to validate
 * @param {Object} options - Validation options
 * @returns {Object} - { valid: boolean, date: Date, error?: string }
 */
export function validateDate(value, options = {}) {
  const { min, max, required = false } = options;

  if (!value) {
    if (required) {
      return { valid: false, date: null, error: 'Date is required' };
    }
    return { valid: true, date: null };
  }

  let date;
  if (value instanceof Date) {
    date = value;
  } else {
    date = new Date(value);
  }

  if (isNaN(date.getTime())) {
    return { valid: false, date: null, error: 'Invalid date' };
  }

  if (min && date < min) {
    return { valid: false, date, error: `Date must be after ${min.toLocaleDateString()}` };
  }

  if (max && date > max) {
    return { valid: false, date, error: `Date must be before ${max.toLocaleDateString()}` };
  }

  return { valid: true, date };
}

/**
 * Validate observation data
 *
 * @param {Object} observation - Observation to validate
 * @returns {Object} - { valid: boolean, sanitized: Object, errors: Object }
 */
export function validateObservation(observation) {
  const errors = {};
  const sanitized = {};

  // Validate activity
  const activity = validateText(observation.activity, {
    required: true,
    minLength: 1,
    maxLength: 500,
  });
  if (!activity.valid) errors.activity = activity.error;
  sanitized.activity = activity.sanitized;

  // Validate energy
  const energy = validateNumber(observation.energy, {
    required: true,
    min: 0,
    max: 100,
    integer: true,
  });
  if (!energy.valid) errors.energy = energy.error;
  sanitized.energy = energy.value;

  // Validate emotion (optional)
  if (observation.emotion) {
    const emotion = validateText(observation.emotion, {
      maxLength: 50,
    });
    if (!emotion.valid) errors.emotion = emotion.error;
    sanitized.emotion = emotion.sanitized;
  }

  // Validate notes (optional)
  if (observation.notes) {
    const notes = validateText(observation.notes, {
      maxLength: 5000,
    });
    if (!notes.valid) errors.notes = notes.error;
    sanitized.notes = notes.sanitized;
  }

  return {
    valid: Object.keys(errors).length === 0,
    sanitized,
    errors,
  };
}

/**
 * Validate experiment data
 *
 * @param {Object} experiment - Experiment to validate
 * @returns {Object} - { valid: boolean, sanitized: Object, errors: Object }
 */
export function validateExperiment(experiment) {
  const errors = {};
  const sanitized = {};

  // Validate curiosity
  const curiosity = validateText(experiment.curiosity, {
    required: true,
    minLength: 10,
    maxLength: 500,
  });
  if (!curiosity.valid) errors.curiosity = curiosity.error;
  sanitized.curiosity = curiosity.sanitized;

  // Validate action
  const action = validateText(experiment.action, {
    required: true,
    minLength: 10,
    maxLength: 500,
  });
  if (!action.valid) errors.action = action.error;
  sanitized.action = action.sanitized;

  // Validate signal
  const signal = validateText(experiment.signal, {
    required: true,
    minLength: 10,
    maxLength: 500,
  });
  if (!signal.valid) errors.signal = signal.error;
  sanitized.signal = signal.sanitized;

  // Validate duration
  const duration = validateNumber(experiment.duration, {
    required: true,
    min: 3,
    max: 90,
    integer: true,
  });
  if (!duration.valid) errors.duration = duration.error;
  sanitized.duration = duration.value;

  return {
    valid: Object.keys(errors).length === 0,
    sanitized,
    errors,
  };
}

/**
 * Sanitize object recursively
 * Sanitizes all string values in an object
 *
 * @param {Object} obj - Object to sanitize
 * @param {Object} options - Sanitization options
 * @returns {Object} - Sanitized object
 */
export function sanitizeObject(obj, options = {}) {
  const { allowHtml = false, maxDepth = 10, currentDepth = 0 } = options;

  if (currentDepth >= maxDepth) {
    console.warn('[Security] Max depth reached in sanitizeObject');
    return obj;
  }

  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === 'string') {
    return allowHtml ? obj : sanitizeHtml(obj);
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item, { ...options, currentDepth: currentDepth + 1 }));
  }

  if (typeof obj === 'object') {
    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
      // Sanitize key
      const sanitizedKey = sanitizeHtml(key);
      sanitized[sanitizedKey] = sanitizeObject(value, { ...options, currentDepth: currentDepth + 1 });
    }
    return sanitized;
  }

  return obj;
}

/**
 * Validate license key format
 *
 * @param {string} licenseKey - License key to validate
 * @returns {Object} - { valid: boolean, sanitized: string, error?: string }
 */
export function validateLicenseKey(licenseKey) {
  if (!licenseKey || typeof licenseKey !== 'string') {
    return { valid: false, sanitized: '', error: 'License key is required' };
  }

  const sanitized = licenseKey.trim().toUpperCase();

  // Check UUID format: XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
  const uuidPattern = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

  if (!uuidPattern.test(sanitized)) {
    return {
      valid: false,
      sanitized,
      error: 'Invalid license key format (should be XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX)',
    };
  }

  return { valid: true, sanitized };
}

/**
 * Create a validator for a schema
 * Returns a validation function
 *
 * @param {Object} schema - Validation schema
 * @returns {Function} - Validation function
 *
 * @example
 * const validateUser = createValidator({
 *   name: { type: 'text', required: true, minLength: 2 },
 *   email: { type: 'email', required: true },
 *   age: { type: 'number', min: 18, max: 120 }
 * });
 *
 * const result = validateUser({ name: 'John', email: 'john@example.com', age: 25 });
 */
export function createValidator(schema) {
  return (data) => {
    const errors = {};
    const sanitized = {};

    for (const [field, rules] of Object.entries(schema)) {
      const value = data[field];

      let result;
      switch (rules.type) {
        case 'text':
          result = validateText(value, rules);
          break;
        case 'email':
          result = validateEmail(value);
          break;
        case 'password':
          result = validatePassword(value, rules);
          break;
        case 'url':
          result = validateUrl(value, rules);
          break;
        case 'number':
          result = validateNumber(value, rules);
          break;
        case 'date':
          result = validateDate(value, rules);
          break;
        default:
          result = { valid: true, sanitized: value };
      }

      if (!result.valid) {
        errors[field] = result.error;
      }

      sanitized[field] = result.sanitized || result.value || result.date;
    }

    return {
      valid: Object.keys(errors).length === 0,
      sanitized,
      errors,
    };
  };
}

export default {
  sanitizeHtml,
  stripHtml,
  hasDangerousContent,
  validateEmail,
  validatePassword,
  validateText,
  validateUrl,
  validateNumber,
  validateDate,
  validateObservation,
  validateExperiment,
  validateLicenseKey,
  sanitizeObject,
  createValidator,
};
